import { useHasAccess } from "../hooks/useHasAccess";
import { useCurrentUserGrants } from "../hooks/useCurrentUserGrants";
import { getWritableScopeIds } from "../lib/getWritableScopeIds";
import { styled } from "goober";
import { useState } from "react";
import { queries } from "../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { useWorkflowZipImport } from "../hooks/useWorkflowZipImport";
import { Button } from "./Button";
import { ChoiceRow } from "./ChoiceRow";
import { Modal } from "./Modal";
import { ModalContent, ModalFooter, ModalHead } from "./ModalCard";
import { PreviewListBox } from "./PreviewList";
import { SpaceDropdown } from "./SpaceDropdown";
import { Text } from "./Text";

export function ImportWorkflowModal({
  file,
  defaultSpaceId,
  integrationSpaceId,
  onIntegrate,
  onClose,
}: {
  file: File;
  defaultSpaceId?: string;
  integrationSpaceId?: string;
  onIntegrate?: () => void;
  onClose: () => void;
}) {
  const hasIntegrationAccess = useHasAccess("space:write", integrationSpaceId);
  const canIntegrate =
    !!integrationSpaceId && hasIntegrationAccess && !!onIntegrate;
  const [destination, setDestination] = useState<
    "This workflow" | "New workflow"
  >("This workflow");
  const integrating = canIntegrate && destination === "This workflow";
  const [spaces] = useSpaces({ settingsView: false });
  const [personalSpace] = useQuery(queries.personalSpace());
  const [preferences] = useQuery(queries.userPreferences());
  const [chosenSpaceId, setChosenSpaceId] = useState<string | null>(null);
  const [grants] = useCurrentUserGrants();
  const writableIds = getWritableScopeIds(
    grants,
    spaces.map((s) => s.id),
    "space:write"
  );
  const members = spaces.filter(
    (s) => isSpaceMember(s) && writableIds.has(s.id)
  );
  const memberId = (id: string | null | undefined) =>
    members.find((s) => s.id === id)?.id;
  const defaultMemberId =
    memberId(defaultSpaceId) ??
    memberId(preferences?.defaultSpace) ??
    memberId(personalSpace?.id) ??
    members[0]?.id ??
    null;
  const selectedSpaceId =
    chosenSpaceId === null
      ? defaultMemberId
      : (memberId(chosenSpaceId) ?? null);
  const { importError, isImporting, importWorkflowZip, canWrite } =
    useWorkflowZipImport(selectedSpaceId);

  return (
    <Modal open onClose={onClose} alert>
      <ModalContent style={{ width: 340 }}>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Import workflow
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            {canIntegrate
              ? `Choose where to bring in the steps from “${file.name}”.`
              : `Create a new workflow from “${file.name}” in the space you choose.`}
          </Text>
        </ModalHead>
        {canIntegrate ? (
          <PreviewListBox role="radiogroup" style={{ maxHeight: "none" }}>
            <ChoiceRow
              selected={integrating}
              label="Add to this workflow"
              description="Attach it to the chat to integrate its steps."
              onPress={() => setDestination("This workflow")}
            />
            <ChoiceRow
              selected={!integrating}
              label="Create a new workflow"
              description="In the space you choose."
              accessory={
                <SpaceDropdown
                  selectedSpaceId={selectedSpaceId}
                  onSelect={setChosenSpaceId}
                  membersOnly
                  writableOnly
                />
              }
              onPress={() => setDestination("New workflow")}
            />
          </PreviewListBox>
        ) : (
          <Field>
            <Text variant="text-small-md" color="var(--text-secondary)">
              Space
            </Text>
            <SpaceDropdown
              selectedSpaceId={selectedSpaceId}
              onSelect={setChosenSpaceId}
              membersOnly
              writableOnly
              variant="field"
            />
          </Field>
        )}
        {importError && (
          <Text variant="text-small" color="var(--text-error)">
            {importError}
          </Text>
        )}
        <ModalFooter>
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="l"
            disabled={isImporting || (!integrating && !canWrite)}
            onClick={async () => {
              if (integrating) {
                if (!canIntegrate || isImporting) return;
                onIntegrate?.();
                onClose();
                return;
              }
              if (await importWorkflowZip(file)) onClose();
            }}
          >
            {integrating ? "Add to workflow" : "Create new"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
